import React, { useState, useEffect } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

// Simple className utility
const cn = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

interface NavSection {
  id: string;
  title: string;
  icon?: React.ReactNode;
}

interface CampaignSideNavProps {
  sections: NavSection[];
  className?: string;
  activeSection?: string;
  onSectionChange?: (sectionId: string) => void;
  errorSections?: string[];
}

const CampaignSideNav: React.FC<CampaignSideNavProps> = ({ 
  sections, 
  className, 
  activeSection = '', 
  onSectionChange,
  errorSections = []
}) => {
  const handleSectionClick = (sectionId: string) => {
    if (onSectionChange) {
      onSectionChange(sectionId);
    }
  };

  return (
    <div className={cn(
      'hidden lg:block w-80 flex-shrink-0',
      className
    )}>
      <div className="w-full">
        <nav className="bg-card border border-border rounded-lg p-6 shadow-sm sticky top-0">
          <h3 className="text-sm font-semibold text-foreground mb-4">
            Campaign Sections
          </h3>
          
          <ul className="space-y-2">
            {sections.map((section) => {
              const isActive = activeSection === section.id;
              const hasError = errorSections.includes(section.id);
              
              return (
                <li key={section.id}>
                  <button
                    onClick={() => handleSectionClick(section.id)}
                    className={cn(
                      'w-full text-left px-4 py-3 rounded-lg text-sm transition-all duration-200 flex items-center justify-between group',
                      isActive
                        ? 'bg-primary text-primary-foreground font-medium shadow-sm'
                        : hasError
                          ? 'text-destructive hover:text-destructive hover:bg-destructive/5 border border-destructive/30'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    )}
                  >
                    <span className="flex items-center">
                      {section.icon && (
                        <span className="mr-2 flex-shrink-0">
                          {section.icon}
                        </span>
                      )}
                      {section.title}
                      {hasError && !isActive && (
                        <span className="ml-2 inline-flex h-2 w-2 rounded-full bg-destructive flex-shrink-0" />
                      )}
                    </span>
                    
                    <ChevronRightIcon 
                      className={cn(
                        'h-4 w-4 transition-transform duration-150',
                        isActive ? 'rotate-90' : 'group-hover:translate-x-1'
                      )}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
          
          <div className="mt-6 pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Click any section to navigate
            </p>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default CampaignSideNav;